import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft, Gavel } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { fetchAllStrikes, type AdminStrike } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { PermissionDenied, Skeleton } from '@/components/States'
import { useAuth } from '@/providers/AuthProvider'

const SEV_COLORS = ['bg-surface-3', 'bg-success', 'bg-accent', 'bg-warning', 'bg-danger', 'bg-danger']

/**
 * A8.19 — Cheklov statistikasi. GET /admin/strikes (≤300) ustidan daraja (1–5) +
 * amaldagi/muddati-tugagan taqsimoti (client-side agregat).
 */
export function StrikeStats() {
  const t = useT()
  const { can } = useAuth()
  const [rows, setRows] = useState<AdminStrike[] | null>(null)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    fetchAllStrikes(ac.signal)
      .then((r) => alive && setRows(r))
      .catch(() => {})
    return () => {
      alive = false
      ac.abort()
    }
  }, [])

  const agg = useMemo(() => {
    const r = rows ?? []
    const bySev = [1, 2, 3, 4, 5].map((s) => ({ sev: s, count: r.filter((x) => x.severity === s).length }))
    return {
      total: r.length,
      active: r.filter((x) => x.active).length,
      expired: r.filter((x) => !x.active).length,
      bySev,
      max: Math.max(1, ...bySev.map((s) => s.count)),
    }
  }, [rows])

  if (!can(PERMISSIONS.USERS_VIEW)) return <PermissionDenied permission={PERMISSIONS.USERS_VIEW} />
  const loading = rows === null

  return (
    <>
      <PageHeader
        title={t.strikes.statsTitle}
        screenId="A8.19"
        actions={
          <Link
            to="/strikes"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.strikes.queue}
          </Link>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="mb-4 flex flex-wrap gap-3">
          <Tile label={t.strikes.total} value={agg.total} loading={loading} icon={<Gavel size={18} className="text-accent" />} />
          <Tile label={t.strikes.active} value={agg.active} loading={loading} tone="danger" />
          <Tile label={t.strikes.expired} value={agg.expired} loading={loading} />
        </div>
        <div className="max-w-md overflow-hidden rounded-md border border-border-default">
          <div className="border-b border-border-default bg-surface-2 px-3 py-2 text-xs font-medium text-tertiary">
            {t.strikes.severity}
          </div>
          {agg.bySev.map((s) => (
            <div key={s.sev} className="flex items-center gap-3 border-b border-border-subtle px-3 py-2 text-sm last:border-0">
              <span className="w-4 font-mono text-tertiary">{s.sev}</span>
              <span className="h-2 flex-1 overflow-hidden rounded-full bg-surface-2">
                {!loading && (
                  <span
                    className={`block h-full rounded-full ${SEV_COLORS[s.sev]}`}
                    style={{ width: `${Math.round((s.count / agg.max) * 100)}%` }}
                  />
                )}
              </span>
              {loading ? <Skeleton className="h-4 w-8" /> : <span className="w-8 text-right font-mono tabular text-secondary">{s.count}</span>}
            </div>
          ))}
        </div>
      </div>
    </>
  )
}

function Tile({
  label,
  value,
  loading,
  tone,
  icon,
}: {
  label: string
  value: number
  loading: boolean
  tone?: 'danger'
  icon?: ReactNode
}) {
  const color = tone === 'danger' ? 'text-danger' : 'text-primary'
  return (
    <div className="inline-flex items-center gap-3 rounded-lg border border-border-default bg-surface-2 px-4 py-3">
      {icon}
      <div>
        <div className="text-xs text-tertiary">{label}</div>
        {loading ? <Skeleton className="mt-1 h-6 w-12" /> : <div className={`font-mono text-xl tabular ${color}`}>{value.toLocaleString()}</div>}
      </div>
    </div>
  )
}
